import React, { useState } from 'react';
import { CheckCheck, ChevronDown } from 'lucide-react';
import { fmtMomento } from '../../lib/format';

/* ===================== VISTO DE LECTURA =====================
   La burbuja de la lista y la línea de estado del lector. El backend manda el
   bloque `visto` colgado de los correos MÍOS (ver `_visto_publico` en server.py):

     { estado: 'enviado' | 'leido', leido_en, via: 'casa' | 'pixel',
       enviado_en, aperturas, primera_apertura, ultima_apertura, aparatos[],
       con_pixel, destinatarios: [{ email, casa, leido_en, via }] }

   Dos vías, y no dicen lo mismo:
     · 'casa'  — el destinatario tiene su buzón aquí: hora y nombre exactos.
     · 'pixel' — es de fuera: se sabe cuándo se abrió el correo, no quién, y solo
       si su cliente carga imágenes.
   La interfaz no promete más de lo que sabe: por eso el detalle lo explica en
   vez de fingir una certeza que no existe.
   ============================================================ */

const persona = (correo) => (correo || '').split('@')[0] || correo;

export function tituloVisto(visto) {
  if (!visto) return '';
  if (visto.estado === 'leido') {
    const veces = visto.aperturas > 1 ? ` · abierto ${visto.aperturas} veces` : '';
    return `Leído ${fmtMomento(visto.leido_en)}${veces}`;
  }
  return `Enviado ${fmtMomento(visto.enviado_en)} · sin leer todavía`;
}

/** Burbuja de la lista: gris = sin leer, azul = leído. */
export function VistoBurbuja({ visto, className = '' }) {
  if (!visto) return null;
  const leido = visto.estado === 'leido';
  return (
    <span
      className={`inline-flex items-center justify-center rounded-full shrink-0 ${className}`}
      title={tituloVisto(visto)}
      aria-label={tituloVisto(visto)}
      data-testid={leido ? 'visto-leido' : 'visto-enviado'}
      style={{
        width: 17,
        height: 17,
        background: leido ? 'var(--accent)' : 'transparent',
        border: `1px solid ${leido ? 'var(--accent)' : 'var(--line)'}`,
        color: leido ? 'var(--accent-foreground)' : 'var(--text-3)',
      }}
    >
      <CheckCheck style={{ width: 10, height: 10 }} strokeWidth={3.5} />
    </span>
  );
}

/** Línea de estado del lector, con el detalle desplegable. */
export function LineaVisto({ visto }) {
  const [abierto, setAbierto] = useState(false);
  if (!visto) return null;

  const leido = visto.estado === 'leido';
  const dests = visto.destinatarios || [];
  const casa = dests.filter((d) => d.casa);
  const fuera = dests.filter((d) => !d.casa);
  const leyeronEnCasa = casa
    .filter((d) => d.leido_en)
    .sort((a, b) => new Date(a.leido_en) - new Date(b.leido_en));

  let titular;
  if (!leido) titular = 'Sin leer todavía';
  else if (visto.via === 'casa' && leyeronEnCasa[0]) {
    titular = `Leído por ${persona(leyeronEnCasa[0].email)} · ${fmtMomento(leyeronEnCasa[0].leido_en)}`;
  } else titular = `Leído · ${fmtMomento(visto.leido_en)}`;

  // A un solo destinatario de fuera sí se le puede poner nombre a la apertura:
  // el correo era suyo y de nadie más. Con varios, no se sabe cuál fue.
  const aperturaExterna = visto.aperturas > 0
    ? (visto.primera_apertura || (visto.via === 'pixel' ? visto.leido_en : null))
    : null;

  const hayDetalle = dests.length > 0 || visto.aperturas > 0;
  const color = leido ? 'var(--accent)' : 'var(--text-3)';

  return (
    <div className="mt-1 mb-2" data-testid="visto-linea">
      <button
        type="button"
        onClick={() => hayDetalle && setAbierto((v) => !v)}
        className="inline-flex items-center gap-2 rounded-full"
        style={{
          background: leido ? 'var(--accent-soft)' : 'var(--bg)',
          border: '1px solid var(--line)',
          padding: '4px 10px 4px 6px',
          fontSize: 12.5,
          color,
          cursor: hayDetalle ? 'pointer' : 'default',
        }}
        data-testid="visto-resumen"
      >
        <VistoBurbuja visto={visto} />
        <span style={{ fontWeight: 600 }}>{titular}</span>
        {hayDetalle && (
          <ChevronDown
            className="h-3.5 w-3.5 transition-transform"
            style={{ opacity: 0.7, transform: abierto ? 'rotate(180deg)' : 'none' }}
          />
        )}
      </button>

      {abierto && (
        <div
          className="mt-2 rounded-xl"
          style={{ background: 'var(--bg)', border: '1px solid var(--line)', padding: '10px 12px', fontSize: 13 }}
          data-testid="visto-detalle"
        >
          {casa.map((d) => (
            <div key={d.email} className="flex items-baseline gap-2 py-0.5">
              <span style={{ color: 'var(--text-2)' }}>{d.email}</span>
              <span style={{ marginLeft: 'auto', color: d.leido_en ? 'var(--accent)' : 'var(--text-3)' }}>
                {d.leido_en ? `leído ${fmtMomento(d.leido_en)}` : 'sin abrir'}
              </span>
            </div>
          ))}

          {fuera.map((d) => (
            <div key={d.email} className="flex items-baseline gap-2 py-0.5">
              <span style={{ color: 'var(--text-2)' }}>{d.email}</span>
              <span
                style={{
                  marginLeft: 'auto',
                  color: aperturaExterna && fuera.length === 1 ? 'var(--accent)' : 'var(--text-3)',
                }}
              >
                {aperturaExterna && fuera.length === 1
                  ? `abierto ${fmtMomento(aperturaExterna)}`
                  : 'sin señal de apertura'}
              </span>
            </div>
          ))}

          {aperturaExterna && fuera.length > 1 && (
            <div className="py-0.5" style={{ color: 'var(--accent)' }}>
              Alguien de fuera lo abrió · {fmtMomento(aperturaExterna)}
            </div>
          )}

          {visto.aperturas > 0 && (
            <div className="pt-1.5 mt-1.5" style={{ borderTop: '1px solid var(--line)', color: 'var(--text-3)' }}>
              Abierto {visto.aperturas} {visto.aperturas === 1 ? 'vez' : 'veces'}
              {visto.ultima_apertura ? ` · última ${fmtMomento(visto.ultima_apertura)}` : ''}
              {(visto.aparatos || []).length > 0 ? ` · desde ${visto.aparatos.join(', ')}` : ''}
            </div>
          )}

          <div className="pt-1.5 mt-1.5" style={{ borderTop: '1px solid var(--line)', color: 'var(--text-3)', lineHeight: 1.45 }}>
            {casa.length > 0 && 'De los buzones de la casa se lee la hora exacta a la que se abrió. '}
            {visto.con_pixel
              ? 'De fuera solo se sabe si el correo se abrió, no quién: quien bloquea imágenes nunca aparecerá como leído.'
              : 'Este correo salió sin formato, así que de los destinatarios de fuera no hay forma de saber si lo abrieron.'}
          </div>
        </div>
      )}
    </div>
  );
}
